import React, {useState, useEffect, useRef} from 'react';
import { withTranslation } from 'react-i18next';
import {Button, Modal, Form, TextArea, Message, Checkbox, Header} from 'semantic-ui-react';
import {colors, fontSizes, spacings, buttons} from 'style';
import {INVITATION_HEADERS, buildInvitationCsv} from './invitationCsv'

const REWARD_HEADERS = ['Tag', 'RewardPoints', 'RewardResources', 'Limit', 'EndsAt']

const INVITATION_CSV_PLACEHOLDER = `${INVITATION_HEADERS.join(',')}
carrot.dev.bonustest,false,500,,,,,,,,,`

const REWARD_CSV_PLACEHOLDER = `${REWARD_HEADERS.join(',')}
carrot.dev.bonustest,0,500,0,`

const EMPTY_INVITE = {
    code: '',
    singleUse: false,
    rewardResources: '',
    invitationReward: '',
    popup: '',
    rewardAction: '',
    segment: '',
    policyHolder: false,
    cohort: '',
    invitationMethod: '',
    onboarding: '',
    joinChallenges: false
}

const style = {
    content: {
        fontSize: fontSizes.body,
        lineHeight: '1.5',
        backgroundColor: colors.themeGrey,
    },
    saveButton: {
        ...buttons.base,
        ...buttons.mediumWidth,
        float: 'right',
        marginLeft: spacings.small,
        marginRight: spacings.small,
    },
    flex: {
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        alignItems: 'center',
        margin: '1rem'
    },
    note: {
        marginTop: spacings.small,
        fontSize: fontSizes.small,
        whiteSpace: 'pre-line'
    },
    csvBlock: {
        marginTop: spacings.small
    },
    section: {
        marginTop: spacings.base,
        paddingTop: spacings.base,
        borderTop: `1px solid ${colors.themeDarkGrey || '#ccc'}`
    }
}

const InvitationUploadForm = ({t, open, close, error, upload}) => {
    const [invite, setInvite] = useState(EMPTY_INVITE)
    const [inviteCsv, setInviteCsv] = useState('')
    const [rewardCsv, setRewardCsv] = useState('')
    const inviteFileSelector = useRef()
    const rewardFileSelector = useRef()

    const hasCode = Boolean(invite.code && String(invite.code).trim())
    const canSave = hasCode || Boolean(inviteCsv && inviteCsv.trim())

    const reset = () => {
        setInvite(EMPTY_INVITE)
        setInviteCsv('')
        setRewardCsv('')
    }

    const cancelModal = () => {
        reset()
        close()
    }

    const readCsvFile = (event, onText) => {
        const [file] = event.target.files
        if (!file) return
        if (file.type && file.type !== 'text/csv' && !file.name?.toLowerCase().endsWith('.csv')) {
            window.alert('Invalid CSV File')
            event.target.value = null
            return
        }
        event.target.value = null
        const reader = new FileReader()
        reader.onerror = (e) => { window.alert(e) }
        reader.onload = (e) => { onText(e.target.result) }
        reader.readAsText(file, 'UTF-8')
    }

    const onChangeInvite = (key) => (e, data) => {
        setInvite((prev) => ({ ...prev, [key]: data.value }))
    }

    const onChangeInviteCheckbox = (key) => (e, data) => {
        setInvite((prev) => ({ ...prev, [key]: data.checked }))
    }

    const onSubmit = async (event) => {
        event && event.preventDefault()
        const invitation = buildInvitationCsv(invite, inviteCsv)
        const rewards = rewardCsv && String(rewardCsv).trim() ? rewardCsv : undefined
        if (!invitation)
            return
        const ok = await upload({ invitation, reward: rewards })
        if (!ok) return
        cancelModal()
    }

    useEffect(() => {
        if (open) {
            reset()
        }
    }, [open])

    return (
        <Modal open={open} style={style.content} onClose={cancelModal} size='large'>
            <Modal.Content>
                <Modal.Description>
                    <Modal.Header>
                        <div style={{...style.flex, fontSize: fontSizes.h4}}>
                            {t('upload_invitation_prompt')}
                        </div>
                    </Modal.Header>
                    <Form onSubmit={onSubmit}>
                        <input
                            ref={inviteFileSelector}
                            type="file"
                            accept=".csv"
                            style={{display: 'none'}}
                            onChange={(event) => readCsvFile(event, setInviteCsv)}
                        />
                        <input
                            ref={rewardFileSelector}
                            type="file"
                            accept=".csv"
                            style={{display: 'none'}}
                            onChange={(event) => readCsvFile(event, setRewardCsv)}
                        />

                        <Header as="h4">{t('single_invitation')}</Header>
                        <Form.Input
                            label={t('Code')}
                            placeholder="carrot.dev.bonustest"
                            value={invite.code}
                            onChange={onChangeInvite('code')}
                        />
                        <Form.Group widths="equal">
                            <Form.Input
                                label={t('RewardResources')}
                                type="number"
                                min="0"
                                placeholder="500"
                                value={invite.rewardResources}
                                onChange={onChangeInvite('rewardResources')}
                            />
                            <Form.Input
                                label={t('InvitationReward')}
                                placeholder={t('InvitationReward_placeholder')}
                                value={invite.invitationReward}
                                onChange={onChangeInvite('invitationReward')}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label={t('Popup')}
                                placeholder={t('Popup_placeholder')}
                                value={invite.popup}
                                onChange={onChangeInvite('popup')}
                            />
                            <Form.Input
                                label={t('RewardAction')}
                                placeholder={t('RewardAction_placeholder')}
                                value={invite.rewardAction}
                                onChange={onChangeInvite('rewardAction')}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label={t('Segment')}
                                value={invite.segment}
                                onChange={onChangeInvite('segment')}
                            />
                            <Form.Input
                                label={t('Cohort')}
                                value={invite.cohort}
                                onChange={onChangeInvite('cohort')}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label={t('InvitationMethod')}
                                value={invite.invitationMethod}
                                onChange={onChangeInvite('invitationMethod')}
                            />
                            <Form.Input
                                label={t('Onboarding')}
                                value={invite.onboarding}
                                onChange={onChangeInvite('onboarding')}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Field>
                                <Checkbox
                                    label={t('Single Use')}
                                    checked={Boolean(invite.singleUse)}
                                    onChange={onChangeInviteCheckbox('singleUse')}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    label={t('Policyholder')}
                                    checked={Boolean(invite.policyHolder)}
                                    onChange={onChangeInviteCheckbox('policyHolder')}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    label={t('JoinChallenges')}
                                    checked={Boolean(invite.joinChallenges)}
                                    onChange={onChangeInviteCheckbox('joinChallenges')}
                                />
                            </Form.Field>
                        </Form.Group>

                        <Header as="h4" style={{marginTop: spacings.base}}>{t('or_invitation_csv_upload')}</Header>
                        <div style={style.csvBlock}>
                            <span>{t('Upload Invitations')}: </span>
                            <Button type="button" onClick={() => inviteFileSelector.current.click()}>
                                {t('Upload Invitations')}
                            </Button>
                        </div>
                        <TextArea
                            rows="8"
                            placeholder={INVITATION_CSV_PLACEHOLDER}
                            value={inviteCsv || ''}
                            onChange={(e) => setInviteCsv(e.target.value)}
                            style={{marginTop: spacings.small}}
                        />
                        <div style={style.note}>{t('upload_invitation_note')}</div>

                        <div style={style.section}>
                            <Header as="h4">{t('upload_reward_prompt')}</Header>
                            <div style={style.csvBlock}>
                                <span>{t('Upload Rewards')}: </span>
                                <Button type="button" onClick={() => rewardFileSelector.current.click()}>
                                    {t('Upload Rewards')}
                                </Button>
                            </div>
                            <TextArea
                                rows="8"
                                placeholder={REWARD_CSV_PLACEHOLDER}
                                value={rewardCsv || ''}
                                onChange={(e) => setRewardCsv(e.target.value)}
                                style={{marginTop: spacings.small}}
                            />
                            <div style={style.note}>{t('upload_reward_note')}</div>
                        </div>

                        {error && (
                            <Message negative>
                                {error?.message || String(error)}
                            </Message>
                        )}
                        <div style={style.flex}>
                            <Button type="button" onClick={cancelModal}>Cancel</Button>
                            <Button style={style.saveButton} disabled={!canSave} type="submit">{t('Save')}</Button>
                        </div>
                    </Form>
                </Modal.Description>
            </Modal.Content>
        </Modal>
    )
}

export default withTranslation('invitations')(InvitationUploadForm)
